import { useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Tabs,
  Tab,
  Chip,
  FormHelperText,
  CircularProgress,
  Stack,
  Alert,
  ListItemText,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
  useConfirmUserOperationMutation,
  useCancelUserOperationMutation,
  usePaymentIntermediariesQuery,
} from './api';
import { usePermissions } from '@/auth/usePermissions';
import type { AddWithdrawFundsRecord, PaymentIntermediariesRecord } from '@/types/user-operations';
import { PaymentIntermediariesStatusEnum } from '@/types/user-operations';
import { appConfig } from '@/api/appConfig';

export type ActionType = 'confirm' | 'reject' | 'view';

export interface OperationDetailsDialogProps {
  record: AddWithdrawFundsRecord;
  actionType: ActionType;
  paymentIntermediaries: PaymentIntermediariesRecord[];
  onClose: (result: AddWithdrawFundsRecord | null) => void;
}

// ── helpers ──────────────────────────────────────────────────────────────────

function deriveOperationType(record: AddWithdrawFundsRecord): string {
  if (record.orderType === 'WITHDRAW_REVENUE') {
    // Angular compat: treat Withdraw Revenue ops like WITHDRAW_FUNDS on the server side
    return 'Withdraw Funds';
  }
  const key = Object.entries({ ADD_FUNDS: 'Add Funds', WITHDRAW_FUNDS: 'Withdraw Funds', ADDRESS_BOOK_TRANSFER: 'Bank Transfer', WITHDRAW_REVENUE: 'Withdraw Revenue' }).find(
    ([, v]) => v === record.orderTypeDescription,
  );
  return key ? key[0] : record.orderType;
}

function getExtIcon(fileName: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  const known = ['pdf', 'bmp', 'doc', 'gif', 'html', 'jpg', 'png', 'ppt', 'svg'];
  const icon = known.includes(ext) ? ext : 'file';
  return `/assets/images/files-icons/${icon}.png`;
}

function getMime(url: string): string {
  const ext = url.split('?')[0].split('.').pop()?.toLowerCase() ?? '';
  return (appConfig.extToMimes as Record<string, string>)[ext] ?? '';
}

// ── Schemas ───────────────────────────────────────────────────────────────────

const confirmSchema = z.object({
  idPaymentIntermediary: z.string().min(1, { message: 'Required' }),
  amountPaid: z.coerce.number().positive({ message: 'Must be greater than 0' }),
  externalReference: z.string().min(1, { message: 'Required' }),
  notes: z.string().optional(),
});

const rejectSchema = z.object({
  idPaymentIntermediary: z.string().optional(),
  amountPaid: z.coerce.number().optional(),
  externalReference: z.string().min(1, { message: 'Required' }),
  notes: z.string().min(1, { message: 'Required' }),
});

type ConfirmFormValues = z.infer<typeof confirmSchema>;
type RejectFormValues = z.infer<typeof rejectSchema>;
type FormValues = ConfirmFormValues & RejectFormValues;

// ── Read-only row ─────────────────────────────────────────────────────────────

function DetailRow({ label, value }: { label: string; value?: string | number | null }) {
  const { t } = useTranslation();
  return (
    <Grid container sx={{ py: 0.5 }}>
      <Grid item xs={6}>
        <Typography variant="body2" fontWeight={500}>
          {label}
        </Typography>
      </Grid>
      <Grid item xs={6}>
        {value != null && value !== '' ? (
          <Typography variant="body2">{value}</Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('pages.userOperations.dialog.not_specified', { defaultValue: 'Not specified' })}
          </Typography>
        )}
      </Grid>
    </Grid>
  );
}

// ── Status chip ───────────────────────────────────────────────────────────────

function StatusChip({ status }: { status: string }) {
  const s = status.toLowerCase();
  const color = s === 'processed' ? 'success' : s === 'pending' ? 'warning' : 'error';
  return <Chip label={status} color={color} size="small" />;
}

// ── View mode ─────────────────────────────────────────────────────────────────

function ViewContent({
  record,
  onClose,
}: {
  record: AddWithdrawFundsRecord;
  onClose: (r: null) => void;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [previewIdx, setPreviewIdx] = useState(0);

  const attachments = (record.attachmentUrl ?? []).map((url, i) => {
    const cleanUrl = url.split('?')[0];
    const ext = cleanUrl.split('.').pop()?.toLowerCase() ?? '';
    return {
      index: i,
      url,
      mime: getMime(url),
      ext,
      extIcon: getExtIcon(cleanUrl),
    };
  });

  const preview = attachments[previewIdx];

  const isAddressBook = record.orderType === 'ADDRESS_BOOK_TRANSFER';
  const showBusiness = !isAddressBook || record.contactType === 'BUSINESS';

  const piObj = !Array.isArray(record.paymentIntermediary) ? record.paymentIntermediary : null;
  const amountToPay = record.amountRequested;
  const amountPaid =
    record.orderType === 'ADD_FUNDS'
      ? record.amountRequested - record.processingFee
      : record.amountRequested + record.processingFee;

  return (
    <Dialog open onClose={() => onClose(null)} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">
          {t(`pages.userOperations.orderTypes.${record.orderType}`, {
            defaultValue: record.orderTypeDescription,
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('pages.userOperations.dialog.operation', { defaultValue: 'Operation' })}{' '}
          {record.uuid}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Tabs value={tab} onChange={(_, v: number) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label={t('pages.userOperations.dialog.details', { defaultValue: 'Details' })} />
          <Tab label={t('pages.userOperations.dialog.attachments', { defaultValue: 'Attachments' })} />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ px: 3, py: 2 }}>
            {showBusiness && (
              <>
                <DetailRow
                  label={t('pages.userOperations.dialog.business_name', { defaultValue: 'Business Name' })}
                  value={record.businessEntityName}
                />
                <DetailRow
                  label={t('pages.userOperations.dialog.address', { defaultValue: 'Address' })}
                  value={record.address}
                />
              </>
            )}
            {record.firstName && (
              <DetailRow
                label={t('pages.userOperations.dialog.firstname', { defaultValue: 'First Name' })}
                value={record.firstName}
              />
            )}
            {record.lastName && (
              <DetailRow
                label={t('pages.userOperations.dialog.lastname', { defaultValue: 'Last Name' })}
                value={record.lastName}
              />
            )}
            <DetailRow
              label={t('pages.userOperations.dialog.amountToPay', { defaultValue: 'Amount to Pay' })}
              value={`XAF ${amountToPay?.toLocaleString()}`}
            />
            <Grid container sx={{ py: 0.5 }}>
              <Grid item xs={6}>
                <Typography variant="body2" fontWeight={500}>
                  {t('pages.userOperations.dialog.status', { defaultValue: 'Status' })}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <StatusChip status={record.status} />
              </Grid>
            </Grid>
            <DetailRow
              label={t('pages.userOperations.dialog.username', { defaultValue: 'Username' })}
              value={record.userName}
            />
            <DetailRow
              label={t('pages.userOperations.dialog.userAccountNumber', { defaultValue: 'Account Number' })}
              value={record.paymentAccountNumber}
            />
            <DetailRow
              label={t('pages.userOperations.dialog.paymentIntermediary', { defaultValue: 'Payment Intermediary' })}
              value={piObj?.name}
            />
            <DetailRow
              label={t('pages.userOperations.dialog.amountPaid', { defaultValue: 'Amount Paid' })}
              value={`XAF ${amountPaid?.toLocaleString()}`}
            />
            <DetailRow
              label={t('pages.userOperations.dialog.paymentReference', { defaultValue: 'Payment Reference' })}
              value={record.externalPaymentReference}
            />
            {record.remark && (
              <DetailRow
                label={t('pages.userOperations.dialog.remark', { defaultValue: 'Remark' })}
                value={record.remark}
              />
            )}
            {record.notes && (
              <DetailRow
                label={t('pages.userOperations.dialog.notes', { defaultValue: 'Notes' })}
                value={record.notes}
              />
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ px: 3, py: 2 }}>
            {attachments.length === 0 ? (
              <Typography color="text.secondary">
                {t('pages.userOperations.dialog.no_attachments', { defaultValue: 'No attachments' })}
              </Typography>
            ) : (
              <>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  {attachments.map((f) => (
                    <Box
                      key={f.index}
                      onClick={() => setPreviewIdx(f.index)}
                      sx={{
                        width: 60,
                        height: 60,
                        border: '2px solid',
                        borderColor: f.index === previewIdx ? 'primary.main' : 'divider',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}
                    >
                      {f.mime.startsWith('image/') ? (
                        <img src={f.url} alt="thumbnail" width={56} height={56} style={{ objectFit: 'cover' }} />
                      ) : (
                        <img src={f.extIcon} alt="file" width={40} height={40} />
                      )}
                    </Box>
                  ))}
                </Box>

                {preview && (
                  <Box sx={{ mb: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      component="a"
                      href={preview.url}
                      download={`operation_${record.uuid}_attachment_${preview.index}.${preview.ext}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('pages.userOperations.dialog.download', { defaultValue: 'Download' })}
                    </Button>
                  </Box>
                )}

                {preview && (
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {preview.mime.startsWith('image/') ? (
                      <img src={preview.url} alt="preview" style={{ maxWidth: '100%' }} />
                    ) : preview.mime ? (
                      <object data={preview.url} type={preview.mime} width="100%" height={400}>
                        <Typography>
                          {t('pages.userOperations.dialog.no_preview', { defaultValue: 'No preview available' })}
                        </Typography>
                      </object>
                    ) : (
                      <img src="/assets/images/no_preview.png" alt="no preview" style={{ maxWidth: 200 }} />
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={() => onClose(null)}>
          {t('pages.userOperations.dialog.close', { defaultValue: 'Close' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Confirm / Reject mode ─────────────────────────────────────────────────────

function ActionContent({
  record,
  actionType,
  paymentIntermediaries: paymentIntermediariesProp,
  onClose,
}: {
  record: AddWithdrawFundsRecord;
  actionType: 'confirm' | 'reject';
  paymentIntermediaries: PaymentIntermediariesRecord[];
  onClose: (r: AddWithdrawFundsRecord | null) => void;
}) {
  const { t } = useTranslation();
  const { hasAny } = usePermissions();
  const confirmMutation = useConfirmUserOperationMutation();
  const cancelMutation = useCancelUserOperationMutation();
  const [files, setFiles] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Re-read the active intermediaries query so we get loading/empty states even
  // when the parent passes a stale prop (TanStack Query dedupes the request).
  const piQuery = usePaymentIntermediariesQuery(PaymentIntermediariesStatusEnum.ACTIVE);
  const paymentIntermediaries = piQuery.data ?? paymentIntermediariesProp;

  // Preselect the intermediary from the record when present; fall back to the
  // legacy Airtable-shaped string array `['records', 'recXXX']` if needed.
  const recordPiId = !Array.isArray(record.paymentIntermediary)
    ? record.paymentIntermediary?.id
    : null;
  const piArray = Array.isArray(record.paymentIntermediary) ? (record.paymentIntermediary as string[]) : null;
  const defaultPiId = recordPiId != null ? String(recordPiId) : piArray && piArray.length > 1 ? piArray[1] : '';

  const amountToPay = record.amountRequested;
  const derivedAmountPaid =
    record.orderType === 'ADD_FUNDS'
      ? record.amountRequested - record.processingFee
      : record.amountRequested + record.processingFee;

  const schema = actionType === 'confirm' ? confirmSchema : rejectSchema;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      idPaymentIntermediary: defaultPiId,
      amountPaid: derivedAmountPaid,
      externalReference: record.externalPaymentReference ?? '',
      notes: record.notes ?? '',
    },
  });

  const operationLabel = t(`pages.userOperations.orderTypes.${record.orderType}`, {
    defaultValue: record.orderTypeDescription,
  });

  const isSubmitting = confirmMutation.isPending || cancelMutation.isPending;
  const isNotAddFunds = deriveOperationType(record) !== 'ADD_FUNDS';

  const removeFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const onInvalid = async () => {
    await Swal.fire({
      icon: 'error',
      title: t('messages.error', { defaultValue: 'Error' }),
      text: t('pages.userOperations.dialog.validationFailed', {
        defaultValue: 'Please fix the highlighted fields',
      }),
    });
  };

  const onSubmit = async (values: FormValues) => {
    if (actionType === 'confirm') {
      if (!files.length) {
        setAttachmentError(true);
        await Swal.fire({
          icon: 'error',
          title: t('messages.error', { defaultValue: 'Error' }),
          text: t('pages.userOperations.dialog.attachmentsRequired', {
            defaultValue: 'Please upload at least one attachment file',
          }),
        });
        return;
      }
      setAttachmentError(false);

      const reqPayload = {
        recordId: record.id,
        uuid: record.uuid,
        idPaymentIntermediary: values.idPaymentIntermediary ?? '',
        amountPaid: values.amountPaid ?? derivedAmountPaid,
        externalReference: values.externalReference ?? '',
        notes: values.notes ?? '',
        operationType: deriveOperationType(record),
      };

      const formData = new FormData();
      files.forEach((f) => formData.append('file[]', f));
      formData.append('confirmUserOperationRequest', JSON.stringify(reqPayload));

      const res = await confirmMutation.mutateAsync(formData);
      if (res.code !== 0) {
        await Swal.fire({
          icon: 'error',
          title: t('messages.error', { defaultValue: 'Error' }),
          text: res.message,
        });
        return;
      }
      await Swal.fire({
        icon: 'success',
        title: t('pages.userOperations.dialog.confirmSuccess', { defaultValue: 'Operation confirmed' }),
        timer: 1500,
        showConfirmButton: false,
      });
      onClose(res.result);
    } else {
      // Confirm with the operator before destructive action
      const proceed = await Swal.fire({
        icon: 'warning',
        title: t('pages.userOperations.dialog.rejectConfirmTitle', {
          defaultValue: 'Reject operation?',
        }),
        text: t('pages.userOperations.dialog.rejectConfirmBody', {
          defaultValue:
            'This will mark the operation as rejected and notify the user. Continue?',
        }),
        showCancelButton: true,
        confirmButtonText: t('pages.userOperations.dialog.rejectConfirmYes', {
          defaultValue: 'Yes, reject',
        }),
        cancelButtonText: t('pages.userOperations.dialog.rejectConfirmNo', {
          defaultValue: 'Cancel',
        }),
        confirmButtonColor: '#d33',
      });
      if (!proceed.isConfirmed) return;

      const reqPayload = {
        recordId: record.id,
        uuid: record.uuid,
        idPaymentIntermediary: null,
        amountPaid: null,
        externalReference: values.externalReference ?? '',
        notes: values.notes ?? '',
        operationType: deriveOperationType(record),
      };

      const res = await cancelMutation.mutateAsync(reqPayload);
      if (res.code !== 0) {
        await Swal.fire({
          icon: 'error',
          title: t('messages.error', { defaultValue: 'Error' }),
          text: res.message,
        });
        return;
      }
      await Swal.fire({
        icon: 'success',
        title: t('pages.userOperations.dialog.rejectSuccess', { defaultValue: 'Operation rejected' }),
        timer: 1500,
        showConfirmButton: false,
      });
      onClose(res.result);
    }
  };

  const canConfirm = hasAny(['edit:ops_confirmUserOp']);
  const canReject = hasAny(['edit:ops_rejectUserOp']);

  const titleKey = actionType === 'confirm'
    ? 'pages.userOperations.dialog.confirmTitle'
    : 'pages.userOperations.dialog.rejectTitle';

  return (
    <Dialog
      open
      onClose={(_, reason) => {
        if (isSubmitting && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
        onClose(null);
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6">
          {t(titleKey, {
            operation: operationLabel,
            defaultValue: `${actionType === 'confirm' ? 'Confirm' : 'Reject'} ${operationLabel}`,
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('pages.userOperations.dialog.operation', { defaultValue: 'Operation' })} {record.uuid}
        </Typography>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} noValidate>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {/* Remark */}
            <Grid item xs={6}>
              <Typography variant="body2" fontWeight={500}>
                {t('pages.userOperations.dialog.remark', { defaultValue: 'Remark' })}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              {record.remark ? (
                <Typography variant="body2" fontWeight={700}>{record.remark}</Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('pages.userOperations.dialog.not_specified', { defaultValue: 'Not specified' })}
                </Typography>
              )}
            </Grid>

            {/* First name */}
            <Grid item xs={6}>
              <Typography variant="body2" fontWeight={500}>
                {t('pages.userOperations.dialog.firstname', { defaultValue: 'First Name' })}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">{record.firstName ?? t('pages.userOperations.dialog.not_specified', { defaultValue: 'Not specified' })}</Typography>
            </Grid>

            {/* Last name */}
            <Grid item xs={6}>
              <Typography variant="body2" fontWeight={500}>
                {t('pages.userOperations.dialog.lastname', { defaultValue: 'Last Name' })}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">{record.lastName ?? t('pages.userOperations.dialog.not_specified', { defaultValue: 'Not specified' })}</Typography>
            </Grid>

            {/* Business name + address for non-ADDRESS_BOOK or BUSINESS contact */}
            {(record.orderType !== 'ADDRESS_BOOK_TRANSFER' || record.contactType === 'BUSINESS') && (
              <>
                <Grid item xs={6}>
                  <Typography variant="body2" fontWeight={500}>
                    {t('pages.userOperations.dialog.business_name', { defaultValue: 'Business Name' })}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">{record.businessEntityName ?? t('pages.userOperations.dialog.not_specified', { defaultValue: 'Not specified' })}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" fontWeight={500}>
                    {t('pages.userOperations.dialog.address', { defaultValue: 'Address' })}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">{record.address ?? t('pages.userOperations.dialog.not_specified', { defaultValue: 'Not specified' })}</Typography>
                </Grid>
              </>
            )}

            {/* Amount to pay (read-only) */}
            <Grid item xs={6}>
              <Typography variant="body2" fontWeight={500}>
                {t('pages.userOperations.dialog.amountToPay', { defaultValue: 'Amount to Pay' })}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">XAF {amountToPay?.toLocaleString()}</Typography>
            </Grid>

            {/* Status (read-only) */}
            <Grid item xs={6}>
              <Typography variant="body2" fontWeight={500}>
                {t('pages.userOperations.dialog.status', { defaultValue: 'Status' })}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <StatusChip status={record.status} />
            </Grid>

            {/* Username */}
            <Grid item xs={6}>
              <Typography variant="body2" fontWeight={500}>
                {t('pages.userOperations.dialog.username', { defaultValue: 'Username' })}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">{record.userName}</Typography>
            </Grid>

            {/* Payment account number (not shown for ADD_FUNDS) */}
            {isNotAddFunds && (
              <>
                <Grid item xs={6}>
                  <Typography variant="body2" fontWeight={500}>
                    {t('pages.userOperations.dialog.userAccountNumber', { defaultValue: 'Account Number' })}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">{record.paymentAccountNumber}</Typography>
                </Grid>
              </>
            )}

            {/* Payment intermediary (confirm only) */}
            {actionType === 'confirm' && (
              <Grid item xs={12}>
                <Controller
                  name="idPaymentIntermediary"
                  control={control}
                  render={({ field }) => {
                    const isLoading = piQuery.isLoading;
                    const isEmpty = !isLoading && paymentIntermediaries.length === 0;
                    return (
                      <FormControl fullWidth size="small" error={!!errors.idPaymentIntermediary || isEmpty}>
                        <InputLabel>
                          {t('pages.userOperations.dialog.paymentIntermediary', { defaultValue: 'Payment Intermediary' })}
                        </InputLabel>
                        <Select
                          {...field}
                          disabled={isLoading || isEmpty}
                          label={t('pages.userOperations.dialog.paymentIntermediary', { defaultValue: 'Payment Intermediary' })}
                          startAdornment={isLoading ? <CircularProgress size={16} sx={{ ml: 1, mr: 1 }} /> : undefined}
                        >
                          {paymentIntermediaries.map((p) => (
                            <MenuItem key={p.id} value={String(p.id)}>
                              <ListItemText
                                primary={p.name}
                                secondary={p.accountNumber}
                                primaryTypographyProps={{ variant: 'body2' }}
                                secondaryTypographyProps={{ variant: 'caption' }}
                              />
                            </MenuItem>
                          ))}
                        </Select>
                        {isLoading && (
                          <FormHelperText>
                            {t('pages.userOperations.dialog.loadingIntermediaries', {
                              defaultValue: 'Loading intermediaries…',
                            })}
                          </FormHelperText>
                        )}
                        {isEmpty && (
                          <FormHelperText>
                            {t('pages.userOperations.dialog.noActiveIntermediaries', {
                              defaultValue: 'No active payment intermediaries found',
                            })}
                          </FormHelperText>
                        )}
                        {errors.idPaymentIntermediary && !isEmpty && !isLoading && (
                          <FormHelperText>{errors.idPaymentIntermediary.message}</FormHelperText>
                        )}
                      </FormControl>
                    );
                  }}
                />
              </Grid>
            )}

            {/* Amount paid (confirm only) */}
            {actionType === 'confirm' && (
              <Grid item xs={12}>
                <TextField
                  {...register('amountPaid')}
                  type="number"
                  fullWidth
                  size="small"
                  label={t('pages.userOperations.dialog.amountPaid', { defaultValue: 'Amount Paid' })}
                  error={!!errors.amountPaid}
                  helperText={
                    errors.amountPaid?.message ??
                    t('pages.userOperations.dialog.amountPaidHint', {
                      defaultValue: 'Auto-calculated from amount requested and processing fee',
                    })
                  }
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
            )}

            {/* External reference */}
            <Grid item xs={12}>
              <TextField
                {...register('externalReference')}
                fullWidth
                size="small"
                label={t('pages.userOperations.dialog.paymentReference', { defaultValue: 'Payment Reference' })}
                placeholder={t('pages.userOperations.dialog.externalReferencePlaceHolder', { defaultValue: 'Enter external reference' })}
                error={!!errors.externalReference}
                helperText={errors.externalReference?.message}
              />
            </Grid>

            {/* File attachments (confirm only) */}
            {actionType === 'confirm' && (
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight={500}>
                      {t('pages.userOperations.dialog.attachments', { defaultValue: 'Attachments' })}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CloudUploadIcon />}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isSubmitting}
                    >
                      {t('pages.userOperations.dialog.selectFiles', { defaultValue: 'Select files' })}
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      {t('pages.userOperations.dialog.attachmentsHint', {
                        defaultValue: 'Allowed: PDF, PNG, JPG, BMP, GIF, SVG',
                      })}
                    </Typography>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/jpg,image/jpeg,image/bmp,image/gif,image/png,image/svg+xml,application/pdf"
                      hidden
                      onChange={(e) => {
                        const next = Array.from(e.target.files ?? []);
                        // Append rather than replace so users can add files incrementally
                        setFiles((prev) => [...prev, ...next]);
                        setAttachmentError(false);
                        // Reset value so re-selecting the same file still fires onChange
                        e.target.value = '';
                      }}
                    />
                  </Box>
                  {files.length === 0 ? (
                    <Typography variant="caption" color={attachmentError ? 'error' : 'text.secondary'}>
                      {attachmentError
                        ? t('pages.userOperations.dialog.attachmentsRequired', {
                            defaultValue: 'Please upload at least one attachment file',
                          })
                        : t('pages.userOperations.dialog.noFilesSelected', {
                            defaultValue: 'No files selected',
                          })}
                    </Typography>
                  ) : (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {files.map((f, idx) => (
                        <Chip
                          key={`${f.name}-${idx}`}
                          label={`${f.name} (${(f.size / 1024).toFixed(1)} KB)`}
                          onDelete={() => removeFile(idx)}
                          deleteIcon={<CloseIcon />}
                          size="small"
                          disabled={isSubmitting}
                        />
                      ))}
                    </Stack>
                  )}
                </Stack>
              </Grid>
            )}

            {/* Notes (reject only) */}
            {actionType === 'reject' && (
              <Grid item xs={12}>
                <TextField
                  {...register('notes')}
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  label={t('pages.userOperations.dialog.notes', { defaultValue: 'Notes' })}
                  error={!!errors.notes}
                  helperText={errors.notes?.message}
                />
              </Grid>
            )}

            {/* Submitting overlay hint */}
            {isSubmitting && (
              <Grid item xs={12}>
                <Alert icon={<CircularProgress size={18} />} severity="info">
                  {t('messages.loading', { defaultValue: 'Processing…' })}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => onClose(null)} disabled={isSubmitting}>
            {t('pages.userOperations.dialog.cancel', { defaultValue: 'Cancel' })}
          </Button>

          {actionType === 'confirm' && canConfirm && (
            <Button
              type="submit"
              variant="contained"
              color="success"
              disabled={isSubmitting || piQuery.isLoading || paymentIntermediaries.length === 0}
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {t('pages.userOperations.dialog.validate', { defaultValue: 'Validate' })}
            </Button>
          )}

          {actionType === 'reject' && canReject && (
            <Button
              type="submit"
              variant="contained"
              color="error"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {t('pages.userOperations.dialog.reject', { defaultValue: 'Reject' })}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}


// ── Public component ──────────────────────────────────────────────────────────

export function OperationDetailsDialog({
  record,
  actionType,
  paymentIntermediaries,
  onClose,
}: OperationDetailsDialogProps) {
  if (actionType === 'view') {
    return <ViewContent record={record} onClose={onClose} />;
  }
  return (
    <ActionContent
      record={record}
      actionType={actionType}
      paymentIntermediaries={paymentIntermediaries}
      onClose={onClose}
    />
  );
}
